// services/UserProfileService.js
class UserProfileService {
  constructor() {
    this.storedSessionId = null; // Will always be created anew on each chat load
  }

  safeGetItem(key) {
    try {
      return localStorage?.getItem?.(key);
    } catch (e) {
      console.warn(`localStorage.getItem(${key}) failed:`, e);
      return null;
    }
  }

  safeSetItem(key, value) {
    try {
      localStorage?.setItem?.(key, value);
    } catch (e) {
      console.warn(`localStorage.setItem(${key}) failed:`, e);
    }
  }

  safeRemoveItem(key) {
    try {
      localStorage?.removeItem?.(key);
    } catch (e) {
      console.warn(`localStorage.removeItem(${key}) failed:`, e);
    }
  }

  storeAuthData(token, source, userId) {
    this.safeSetItem('token', token);
    this.safeSetItem('source', source);
    this.safeSetItem('tokenType', "Bearer");
    this.safeSetItem('userId', userId);

    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    url.searchParams.delete('source');
    window.history.replaceState({}, '', url);
  }

  async fetchUserProfile() {
    const urlParams = new URLSearchParams(window.location.search);
    let token = urlParams.get('token');
    let source = urlParams.get('source');

    if (!token) token = this.safeGetItem('token');
    if (!source) source = this.safeGetItem('source');

    if (token && source) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'source': source
          }
        });

        if (response.ok) {
          const data = await response.json();
          const sessionId = this.generateSessionId(data.email, data.id);

          this.setSessionId(sessionId);

          if (urlParams.get('token')) {
            this.storeAuthData(token, source, data.id);
          }

          return {
            profile: data,
            isAuthorized: true,
            isProfileLoaded: true
          };
        } else if (response.status === 401) {
          this.clearAuthData();
          return {
            profile: null,
            isAuthorized: false,
            isProfileLoaded: false
          };
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        return {
          profile: null,
          isAuthorized: false,
          isProfileLoaded: false
        };
      }
    }

    return {
      profile: null,
      isAuthorized: false,
      isProfileLoaded: false
    };
  }

  clearAuthData() {
    this.safeRemoveItem('token');
    this.safeRemoveItem('tokenType');
    this.safeRemoveItem('source');
    this.safeRemoveItem('userId');
  }

  generateSessionId(email, userId) {
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    const timestamp =
      now.getFullYear().toString() +
      pad(now.getMonth() + 1) +
      pad(now.getDate()) +
      pad(now.getHours()) +
      pad(now.getMinutes()) +
      pad(now.getSeconds());

      const sessionId = `user_${email}_${userId}_${timestamp}`;
      this.setSessionId(sessionId);
  }

  setSessionId(sessionId) {
    this.storedSessionId = sessionId;
    this.safeSetItem('sessionId', sessionId);
  }

  getSessionId() {
    return this.storedSessionId;
  }

  setProfile(profile) {
    this.safeSetItem("profile", JSON.stringify(profile));
  }

  getProfile(){
    return this.safeGetItem("profile");
  }
}

export default new UserProfileService();
